import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import AdminNav from '../components/AdminNav'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const GREEN  = '#16A34A'
const RED    = '#DC2626'

const ALL_PERMISSIONS = [
  { key: 'sellers',     label: 'Sellers',     desc: 'View and manage seller accounts' },
  { key: 'finance',     label: 'Finance',     desc: 'Revenue, payouts, invoices' },
  { key: 'boosts',      label: 'AdBoosts',    desc: 'Review and manage boost campaigns' },
  { key: 'flags',       label: 'Flags',       desc: 'Customer flag oversight' },
  { key: 'referrals',   label: 'Referrals',   desc: 'Manage referral payouts' },
  { key: 'help',        label: 'Help',        desc: 'Respond to support requests' },
  { key: 'marketing',   label: 'Marketing',   desc: 'Announcements and campaigns' },
  { key: 'settings',    label: 'Settings',    desc: 'Platform-wide settings' },
]

function Icon({ path, size = 15, color = MUTED, sw = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  )
}

function ToggleSwitch({ enabled, onChange, disabled }) {
  return (
    <button onClick={onChange} disabled={disabled}
      style={{ width: '36px', height: '20px', borderRadius: '10px', border: 'none', background: enabled ? BLUE : BORDER, cursor: disabled ? 'not-allowed' : 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, opacity: disabled ? 0.5 : 1 }}>
      <div style={{ position: 'absolute', top: '2px', left: enabled ? '18px' : '2px', width: '16px', height: '16px', borderRadius: '50%', background: WHITE, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
  )
}

export default function AdminUsers() {
  const [admins, setAdmins]           = useState([])
  const [loading, setLoading]         = useState(true)
  const [saving, setSaving]           = useState(null)
  const [removing, setRemoving]       = useState(null)
  const [confirmRemove, setConfirmRemove] = useState(null)
  const [showCreate, setShowCreate]   = useState(false)
  const [createForm, setCreateForm]   = useState({ email: '', name: '', password: '', is_super_admin: false })
  const [creating, setCreating]       = useState(false)
  const [createError, setCreateError] = useState('')
  const [expandedId, setExpandedId]   = useState(null)
  const [savingPerms, setSavingPerms] = useState(null)
  const [localPerms, setLocalPerms]   = useState({}) // adminId -> permissions array

  useEffect(() => { fetchAdmins() }, [])

  const fetchAdmins = async () => {
    setLoading(true)
    const { data } = await supabase.from('admin_users').select('*').order('created_at', { ascending: true })
    const arr = data || []
    setAdmins(arr)
    // Init local perms
    const permsMap = {}
    arr.forEach(a => { permsMap[a.id] = a.permissions || [] })
    setLocalPerms(permsMap)
    setLoading(false)
  }

  const handleCreate = async () => {
    if (!createForm.email || !createForm.password) { setCreateError('Email and password are required'); return }
    if (createForm.password.length < 8) { setCreateError('Password must be at least 8 characters'); return }
    setCreating(true)
    setCreateError('')
    try {
      // Hash password using SHA-256 (basic — for production use bcrypt via edge function)
      const encoder = new TextEncoder()
      const data = encoder.encode(createForm.password)
      const hashBuffer = await crypto.subtle.digest('SHA-256', data)
      const hashArray  = Array.from(new Uint8Array(hashBuffer))
      const hash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

      const { error } = await supabase.from('admin_users').insert({
        email: createForm.email.toLowerCase().trim(),
        name: createForm.name || null,
        password_hash: hash,
        is_super_admin: createForm.is_super_admin,
        permissions: createForm.is_super_admin ? ALL_PERMISSIONS.map(p => p.key) : [],
        role: createForm.is_super_admin ? 'super_admin' : 'admin',
        is_active: true,
      })
      if (error) { setCreateError(error.message); setCreating(false); return }
      await fetchAdmins()
      setShowCreate(false)
      setCreateForm({ email: '', name: '', password: '', is_super_admin: false })
    } catch (e) {
      setCreateError(e.message)
    }
    setCreating(false)
  }

  const handleTogglePermission = (adminId, permKey) => {
    setLocalPerms(prev => {
      const current = prev[adminId] || []
      const updated  = current.includes(permKey) ? current.filter(p => p !== permKey) : [...current, permKey]
      return { ...prev, [adminId]: updated }
    })
  }

  const handleSelectAll = (adminId) => {
    setLocalPerms(prev => ({ ...prev, [adminId]: ALL_PERMISSIONS.map(p => p.key) }))
  }

  const handleSavePermissions = async (adminId) => {
    setSavingPerms(adminId)
    await supabase.from('admin_users').update({ permissions: localPerms[adminId] || [] }).eq('id', adminId)
    setSavingPerms(null)
    await fetchAdmins()
  }

  const handleToggleActive = async (admin) => {
    setSaving(admin.id)
    await supabase.from('admin_users').update({ is_active: !admin.is_active }).eq('id', admin.id)
    await fetchAdmins()
    setSaving(null)
  }

  const handleRemove = async (admin) => {
    setRemoving(admin.id)
    await supabase.from('admin_users').delete().eq('id', admin.id)
    await fetchAdmins()
    setConfirmRemove(null)
    setRemoving(null)
  }

  const inputStyle = { width: '100%', border: '1px solid ' + BORDER, borderRadius: '9px', padding: '9px 12px', fontSize: '12.5px', outline: 'none', background: BG, color: TEXT, fontFamily: 'Space Grotesk, sans-serif', boxSizing: 'border-box' }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <AdminNav />
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ padding: '24px 28px' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: TEXT }}>Admin Users</div>
              <div style={{ fontSize: '12.5px', color: MUTED, marginTop: '2px' }}>Manage team access and permissions</div>
            </div>
            <button onClick={() => setShowCreate(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '10px', background: BLUE, border: 'none', fontSize: '13px', fontWeight: 600, color: WHITE, cursor: 'pointer' }}>
              <Icon path="M12 4v16m8-8H4" color={WHITE} size={14} sw={2.5} />
              Add Admin
            </button>
          </div>

          {/* Info banner */}
          <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', fontSize: '12px', color: '#1E40AF', lineHeight: 1.6 }}>
            <strong>Super admins</strong> have full access to everything and cannot be permission-restricted. Regular admins can only access the sections you enable for them. Share passwords securely — admins can change their own password after first login.
          </div>

          {loading ? (
            <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '48px', textAlign: 'center', color: MUTED }}>Loading...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {admins.map(admin => {
                const isExpanded  = expandedId === admin.id
                const perms       = localPerms[admin.id] || []
                const origPerms   = admin.permissions || []
                const hasChanges  = JSON.stringify([...perms].sort()) !== JSON.stringify([...origPerms].sort())

                return (
                  <div key={admin.id} style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                    {/* Admin row */}
                    <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                      {/* Avatar */}
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: admin.is_super_admin ? '#FEF3C7' : '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700, color: admin.is_super_admin ? '#D97706' : BLUE, flexShrink: 0 }}>
                        {admin.name?.charAt(0).toUpperCase() || admin.email?.charAt(0).toUpperCase() || 'A'}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '3px' }}>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: TEXT }}>{admin.name || admin.email}</span>
                          {admin.is_super_admin && (
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '20px', background: '#FEF3C7', color: '#D97706' }}>⭐ SUPER ADMIN</span>
                          )}
                          <span style={{ fontSize: '10.5px', fontWeight: 600, padding: '1px 7px', borderRadius: '20px', background: admin.is_active ? '#F0FDF4' : '#F5F5F5', color: admin.is_active ? GREEN : MUTED }}>
                            {admin.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: MUTED }}>
                          {admin.name ? admin.email : ''}
                          {admin.last_login && <span style={{ marginLeft: admin.name ? '12px' : '0' }}>Last login: {new Date(admin.last_login).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: '2-digit' })}</span>}
                          {!admin.is_super_admin && <span style={{ marginLeft: '12px' }}>{perms.length} of {ALL_PERMISSIONS.length} permissions</span>}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        {!admin.is_super_admin && (
                          <button onClick={() => setExpandedId(isExpanded ? null : admin.id)}
                            style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid ' + BORDER, background: isExpanded ? '#EFF6FF' : BG, fontSize: '12px', fontWeight: 600, color: isExpanded ? BLUE : MUTED, cursor: 'pointer' }}>
                            {isExpanded ? 'Done' : 'Permissions'}
                          </button>
                        )}
                        <ToggleSwitch enabled={admin.is_active} onChange={() => handleToggleActive(admin)} disabled={saving === admin.id || admin.is_super_admin} />
                        {!admin.is_super_admin && (
                          <button onClick={() => setConfirmRemove(admin)}
                            style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #FECACA', background: '#FEF2F2', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon path="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" color={RED} size={13} sw={2} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Permissions panel */}
                    {isExpanded && !admin.is_super_admin && (
                      <div style={{ borderTop: '1px solid ' + BORDER, padding: '16px 20px', background: BG }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Permissions</div>
                          <button onClick={() => handleSelectAll(admin.id)}
                            style={{ fontSize: '11.5px', fontWeight: 600, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: '0' }}>
                            Select all
                          </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
                          {ALL_PERMISSIONS.map(perm => {
                            const enabled = perms.includes(perm.key)
                            return (
                              <div key={perm.key} onClick={() => handleTogglePermission(admin.id, perm.key)}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: '10px', background: enabled ? '#EFF6FF' : WHITE, border: '1px solid ' + (enabled ? BLUE + '40' : BORDER), cursor: 'pointer', transition: 'all 0.12s' }}>
                                <div>
                                  <div style={{ fontSize: '13px', fontWeight: 600, color: enabled ? BLUE : TEXT }}>{perm.label}</div>
                                  <div style={{ fontSize: '11px', color: MUTED, marginTop: '1px' }}>{perm.desc}</div>
                                </div>
                                <ToggleSwitch enabled={enabled} onChange={() => handleTogglePermission(admin.id, perm.key)} />
                              </div>
                            )
                          })}
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <button onClick={() => handleSavePermissions(admin.id)} disabled={!hasChanges || savingPerms === admin.id}
                            style={{ padding: '9px 20px', borderRadius: '9px', background: hasChanges ? BLUE : BORDER, border: 'none', fontSize: '13px', fontWeight: 600, color: WHITE, cursor: hasChanges ? 'pointer' : 'not-allowed', opacity: savingPerms === admin.id ? 0.7 : 1 }}>
                            {savingPerms === admin.id ? 'Saving...' : hasChanges ? 'Save Permissions' : 'No Changes'}
                          </button>
                          {hasChanges && (
                            <button onClick={() => setLocalPerms(prev => ({ ...prev, [admin.id]: admin.permissions || [] }))}
                              style={{ padding: '9px 16px', borderRadius: '9px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
                              Reset
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}

              {admins.length === 0 && (
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '48px', textAlign: 'center' }}>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>👤</div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: TEXT, marginBottom: '4px' }}>No admin users yet</div>
                  <div style={{ fontSize: '12px', color: MUTED }}>Create your first admin account above</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create admin modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}
          onClick={e => e.target === e.currentTarget && setShowCreate(false)}>
          <div style={{ background: WHITE, borderRadius: '16px', padding: '28px', maxWidth: '440px', width: '100%', fontFamily: 'Space Grotesk, sans-serif' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: TEXT, marginBottom: '16px' }}>Add Admin User</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Email *</label>
                <input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="admin@example.com" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
              </div>
              <div>
                <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Name <span style={{ fontWeight: 400, color: '#B0B3B8' }}>optional</span></label>
                <input type="text" value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Admin's name" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
              </div>
              <div>
                <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Temporary Password *</label>
                <input type="text" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Min 8 characters — share securely" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                <div style={{ fontSize: '11px', color: MUTED, marginTop: '4px' }}>Share this password with the admin. They can change it after login.</div>
              </div>

              {/* Super admin toggle */}
              <div onClick={() => setCreateForm(f => ({ ...f, is_super_admin: !f.is_super_admin }))}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: '10px', border: '1px solid ' + (createForm.is_super_admin ? '#FDE68A' : BORDER), background: createForm.is_super_admin ? '#FFFBEB' : BG, cursor: 'pointer' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: createForm.is_super_admin ? '#D97706' : TEXT }}>Super Admin</div>
                  <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>Full access to everything — cannot be restricted</div>
                </div>
                <ToggleSwitch enabled={createForm.is_super_admin} onChange={() => setCreateForm(f => ({ ...f, is_super_admin: !f.is_super_admin }))} />
              </div>
            </div>

            {createError && (
              <div style={{ padding: '10px 12px', borderRadius: '9px', background: '#FEF2F2', border: '1px solid #FECACA', fontSize: '12px', color: RED, marginBottom: '14px' }}>
                {createError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setShowCreate(false); setCreateError(''); setCreateForm({ email: '', name: '', password: '', is_super_admin: false }) }}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleCreate} disabled={creating}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', background: creating ? BORDER : BLUE, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: creating ? 'not-allowed' : 'pointer' }}>
                {creating ? 'Creating...' : 'Create Admin'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm remove modal */}
      {confirmRemove && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: WHITE, borderRadius: '16px', padding: '28px', maxWidth: '360px', width: '100%', fontFamily: 'Space Grotesk, sans-serif', textAlign: 'center' }}>
            <div style={{ fontSize: '36px', marginBottom: '10px' }}>🗑️</div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: TEXT, marginBottom: '6px' }}>Remove Admin?</div>
            <div style={{ fontSize: '12.5px', color: MUTED, marginBottom: '6px' }}>{confirmRemove.name || confirmRemove.email}</div>
            <div style={{ fontSize: '12px', color: MUTED, marginBottom: '20px', lineHeight: 1.6 }}>This admin will lose all access immediately. This cannot be undone.</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setConfirmRemove(null)}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '13px', fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={() => handleRemove(confirmRemove)} disabled={removing === confirmRemove.id}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', background: RED, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: 'pointer', opacity: removing === confirmRemove.id ? 0.6 : 1 }}>
                {removing === confirmRemove.id ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}